import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const bodyParts = [
    { name: "Eye", image: "eye.png" },
    { name: "Ear", image: "ear.png" },
    { name: "Hand", image: "hand.png" },
    { name: "Nose", image: "nose.png" },
    { name: "Mouth", image: "mouth.png" },
    { name: "Foot", image: "foot.png" },
    { name: "Head", image: "head.png" },
    { name: "Leg", image: "leg.png" },
    { name: "Arm", image: "arm.png" },
    { name: "Finger", image: "finger.png" },
    { name: "Toe", image: "toe.png" },
    { name: "Knee", image: "knee.png" },
    { name: "Elbow", image: "elbow.png" },
    { name: "Shoulder", image: "shoulder.png" },
    { name: "Neck", image: "neck.png" },
    { name: "Back", image: "back.png" },
    { name: "Chest", image: "chest.png" },
    { name: "Belly", image: "belly.png" },
    { name: "Tongue", image: "tongue.png" },
    { name: "Teeth", image: "teeth.png" },
    { name: "Palm", image: "palm.png" },
    { name: "Heel", image: "heel.png" },
    { name: "Eyebrow", image: "eyebrow.png" },
    { name: "Eyelashes", image: "eyelashes.png" }
];

const getRandomUniqueOptions = (correctOption: any) => {
    const options = [correctOption];
    const availableOptions = bodyParts.filter(part => part.name !== correctOption.name);
    while (options.length < 9) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const shuffleArray = (array: any[]) => {
    return array.sort(() => Math.random() - 0.5);
};

const BodyPartsQuiz = () => {
    const getNewQuestion = () => {
        document.title = "Choose the Correct Body Part – Nursery Worksheet | Mama’s Guidance"; // Set page title
        const newQuestion = bodyParts[Math.floor(Math.random() * bodyParts.length)];
        return { newQuestion, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            nextQuestion()
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {

            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);

        }, 5000)
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids learn about the human body with the "Choose the Correct Body Part" activity on Mamas Guidance—an interactive and fun way to identify body parts and enhance early science and self-awareness skills.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Choose the Correct Body Part</h1>

                                    <p>
                                        Help your child learn about their body in a fun and interactive way with our <strong>Choose the Correct Body Part</strong> worksheet! This EVS (Environmental Science) activity is designed for nursery children to <strong>recognize body parts by name and picture</strong>.
                                    </p>

                                    <h2>🖐️ What’s in the Worksheet?</h2>
                                    <p>
                                        Children will see pictures of familiar body parts such as <b><em>eyes, ears, nose, hands, feet, mouth, etc.</em></b>. Each question asks them to <strong>choose or circle the correct body part</strong> from two or more options.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify and name body parts</li>
                                        <li>Improve picture-word association</li>
                                        <li>Enhance vocabulary and language development</li>
                                        <li>Strengthen observation and cognitive skills</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use</h2>
                                    <ul>
                                        <li>Read the prompt or question aloud (e.g., <strong>&quot;Which is the nose?&quot;</strong>).</li>
                                        <li>Let the child observe the pictures and choose the correct one.</li>
                                        <li>Ask them to circle or tick the right answer.</li>
                                        <li>Reinforce by pointing to their own body part and repeating the name.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/environmental-science/animal/match-animal"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Animal</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/environmental-science/fruit/match-fruit"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Fruit</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/environmental-science/vegetables/match-vegetable"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Vegetable</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Is Important</h2>
                                    <p>
                                        Understanding body parts is a basic life skill. This worksheet helps children <strong>recognize their body</strong>, <strong>improves their communication</strong>, and <strong>builds early science awareness</strong> in a relatable way.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Choose the Correct Body Part</h1>

                                <p>
                                    Help your child learn about their body in a fun and interactive way with our <strong>Choose the Correct Body Part</strong> worksheet! This EVS (Environmental Science) activity is designed for nursery children to <strong>recognize body parts by name and picture</strong>.
                                </p>

                                <h2>🖐️ What’s in the Worksheet?</h2>
                                <p>
                                    Children will see pictures of familiar body parts such as <b><em>eyes, ears, nose, hands, feet, mouth, etc.</em></b>. Each question asks them to <strong>choose or circle the correct body part</strong> from two or more options.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify and name body parts</li>
                                    <li>Improve picture-word association</li>
                                    <li>Enhance vocabulary and language development</li>
                                    <li>Strengthen observation and cognitive skills</li>
                                </ul>

                                <h2>👩‍🏫 How to Use</h2>
                                <ul>
                                    <li>Read the prompt or question aloud (e.g., <strong>&quot;Which is the nose?&quot;</strong>).</li>
                                    <li>Let the child observe the pictures and choose the correct one.</li>
                                    <li>Ask them to circle or tick the right answer.</li>
                                    <li>Reinforce by pointing to their own body part and repeating the name.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/environmental-science/animal/match-animal"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Animal</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/fruit/match-fruit"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Fruit</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/vegetables/match-vegetable"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Vegetable</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Understanding body parts is a basic life skill. This worksheet helps children recognize their body, improves their communication, and builds early science awareness in a relatable way.
                                </p>
                            </article>
                            <div className="mt-5 mb-5 text-center">
                                <h2 className="mt-2 mb-4">Find the {question.name}</h2>
                                <div className="">
                                    {options.map((option, index) => (
                                        <img style={{ marginBottom: '2rem' }} key={index} src={process.env.REACT_APP_API_URL + option.image} onClick={() => handleAnswer(option.name)} className="mb-4 clickable-image my-thumbnail" />
                                    ))}
                                </div>
                                <h3>{feedback}</h3>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default BodyPartsQuiz;
